import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSuperApp } from '../hooks/useSuperApp';

/**
 * ProtectedRoute Component
 *
 * Protects routes that require authentication.
 * If user is not authenticated:
 * - Dispatches auth:loginRequired event with the intended URL
 * - Redirects to home page
 *
 * AuthGuard will show the login modal and navigate to the intended URL after login.
 */
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const { currentUser: user, isAuthLoading: isLoading } = useSuperApp();

  // Build the full URL to redirect back to after login
  const intendedUrl = `${location.pathname}${location.search}`;

  useEffect(() => {
    // Only dispatch event when we're sure there's no user (not loading)
    if (!isLoading && !user) {
      window.dispatchEvent(new CustomEvent('auth:loginRequired', {
        detail: { redirectTo: intendedUrl }
      }));
    }
  }, [isLoading, user, intendedUrl]);

  // While loading, show nothing (or could show a loading spinner)
  if (isLoading) {
    return null;
  }

  // If no user, redirect to home (modal will be shown by AuthGuard)
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated, render children
  return children;
};

export default ProtectedRoute;
